import { ReferenceData } from "@bitwarden/web-vault/app/models/data/blobby/reference-data.data";
import { BalanceByAccountsSymbols } from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";
import {
  CreatedRecords,
  EstimateActionProperties,
  ScenarioOptionWinner,
  ScenarioPermutation,
} from "@bitwarden/web-vault/app/models/types/scenario-group.types";
import { SplitCategory } from "@bitwarden/web-vault/app/models/data/shared/split-category/split-category";
import { SplitClassificationType } from "@bitwarden/web-vault/app/models/types/split-classification-type";
import { TransactionBalanceHelpers } from "@bitwarden/web-vault/app/services/DataCalculationService/transactionBalances/transactionBalanceHelpers";
import { WebWorkerQueue } from "@bitwarden/web-vault/app/services/web-worker/WebWorkerQueue";
import { EstimateActionWorkerMessage } from "@bitwarden/web-vault/app/services/web-worker/estimate-actions/estimate-action.worker.message";
import { Injector } from "@angular/core";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { ScenarioGroupView } from "@bitwarden/web-vault/app/models/view/scenario-group/scenario-group.view";
import { sortTransactionViews } from "@bitwarden/web-vault/app/shared/utils/helper.transactionView/sort";
import { ConsoleLogService } from "@bitwarden/common/services/consoleLog.service";

export class ScenarioOption {
  permutations: Array<ScenarioPermutation>;
  winner: ScenarioOptionWinner;

  userEstimateTransactions: Array<TransactionView>;
  startingTransactions: Array<TransactionView>;
  startingAccountBalances: BalanceByAccountsSymbols;
  runningAccountBalances: BalanceByAccountsSymbols;

  defaultSplitClassification: SplitClassificationType[];
  defaultSplitCategory: SplitCategory[];
  baseCurrency: string;
  referenceData: ReferenceData[];

  scenarioGroup: ScenarioGroupView;
  startDate: Date;
  endDate: Date;
  completedPemutations: number;
  totalPermutations: number;

  webWorkerQueue: WebWorkerQueue;
  injector: Injector;
  callback: (winner: ScenarioOptionWinner) => void;
  completePermutation: () => void;
  private readonly logger = new ConsoleLogService(false);

  constructor(
    scenarioGroup: ScenarioGroupView,
    webWorkerQueue: WebWorkerQueue,
    startingBalances: BalanceByAccountsSymbols,
    injector: Injector,
  ) {
    this.scenarioGroup = scenarioGroup;
    this.startDate = new Date(scenarioGroup.anchorPoint.date);
    this.startDate.setHours(0, 0, 0, 0); // Set to start of the day
    this.endDate = new Date(scenarioGroup.endDate.date);
    this.webWorkerQueue = webWorkerQueue;
    this.injector = injector;

    if (startingBalances) {
      this.startingAccountBalances = startingBalances;
      this.runningAccountBalances = startingBalances;
    }
  }

  async beforeRunningPermutationsHook() {
    this.userEstimateTransactions = await this.getUserGeneratedEstimateTransactions();
    await this.createPermutations();

    // set the startingBalance
  }

  async afterRunningPermutationsHook() {
    this.winner = await this.calculateWinner();
    this.callback(this.winner);
  }

  /**
   * createPermutations - Create the different possible sets of permutations to run
   *                      By default, this should only run the ones from blobby so that it will
   *                      work for user generated scenarios eventually
   */
  async createPermutations() {
    // TODO: Update this to create a single permutation that runs the estimate actions that are saved to Blobby
    this.permutations = [];
  }

  /**
   * calculateWinner - choose the winning permutation from the set
   */
  async calculateWinner(): Promise<ScenarioOptionWinner> {
    // if only 1 permuatation that that is always the winner
    if (this.permutations.length === 1) {
      const winningPermutation = this.permutations[0];
      const finalBalance = await this.getBalanceForPermutation(winningPermutation);
      const createdRecords = this.combineCreatedRecords(winningPermutation);
      const fullTransactionList = this.getFullTransactionList(createdRecords.transactions);
      fullTransactionList.sort(sortTransactionViews);
      const scenarioHelp = winningPermutation.scenarioHelpInfo;
      return {
        createdRecords: createdRecords,
        fullTransactionList: fullTransactionList,
        finalBalanceAmount: finalBalance,
        helpInfo: scenarioHelp,
      };
    }
  }

  /**
   * combineCreatedRecords - Combines all the created records from each estimate action that was run for a
   *                        permutation
   * @param permutation
   */
  combineCreatedRecords(permutation: ScenarioPermutation): CreatedRecords {
    const createdRecords: CreatedRecords = {
      transactions: [],
      accounts: [],
    };
    for (const estimateAction of permutation.estimateActions) {
      if (estimateAction?.createdRecords?.transactions) {
        createdRecords.transactions = createdRecords.transactions.concat(
          estimateAction.createdRecords.transactions,
        );
      }
      if (estimateAction?.createdRecords?.accounts) {
        createdRecords.accounts = createdRecords.accounts.concat(
          estimateAction.createdRecords.accounts,
        );
      }
    }
    return createdRecords;
  }

  getFullTransactionList(transactions: Array<TransactionView>): Array<TransactionView> {
    return [...this.startingTransactions, ...this.userEstimateTransactions, ...transactions];
  }

  /**
   *
   */
  async getBalanceForPermutation(permutation: ScenarioPermutation): Promise<number> {
    if (permutation.estimateActions.length > 0) {
      const finalBalanceArray =
        permutation.estimateActions[permutation.estimateActions.length - 1].runningAccountBalances;

      const transactionBalanceHelpers = new TransactionBalanceHelpers();
      return this.roundBalance(
        transactionBalanceHelpers.sumAccountBalances(finalBalanceArray, this.scenarioGroup.symbol),
      );
    }
  }

  async run(
    callback: (winner: ScenarioOptionWinner) => void,
    completePermutation: () => void,
  ): Promise<void> {
    this.callback = callback;
    this.completePermutation = completePermutation;

    if (this.permutations.length === 0) {
      await this.completedRunningAllPermutations();
      return;
    }

    // run through each possible permutation of the scenario
    this.totalPermutations = this.permutations.length;
    this.completedPemutations = 0;
    let i = 0;
    for (const permutation of this.permutations) {
      const createdRecords: CreatedRecords = {
        runningAccountBalances: this.runningAccountBalances,
      };

      //TODO @Michelle@Sinan : I need 0 transactions if the balance is negative or 0 . Is this the right way ?
      // Moved this to run in the
      // const hasPositiveAccount = await this.hasPositiveBalanceAccounts(groupedBalance);
      // run all the estimate actions for each permutation
      if (permutation.estimateActions.length > 0) {
        await this.runNextEstimateAction(
          i,
          0,
          permutation.estimateActions,
          createdRecords,
          this.returnFromEstimateAction.bind(this),
        );
      } else {
        this.completedPemutations++;
        this.completePermutation();

        if (this.totalPermutations === this.completedPemutations) {
          await this.completedRunningAllPermutations();
        }
      }
      i++;
    }
  }

  /**
   * runNextEstimateAction - Queue the next estimate action in the permutation for a webwoker to take care of
   * @param permutationIndex
   * @param estimateIndex
   * @param estimateActions
   * @param createdRecords
   * @param callback
   */
  async runNextEstimateAction(
    permutationIndex: number,
    estimateIndex: number,
    estimateActions: EstimateActionProperties[],
    createdRecords: CreatedRecords,
    callback: (data: any) => void,
  ) {
    const estimateActionParams = estimateActions[estimateIndex];
    const estimateActionData = {
      estimateActionParams: estimateActionParams,
      estimateActions: estimateActions,
      createdRecords: createdRecords,
      permutationIndex: permutationIndex,
      estimateIndex: estimateIndex,
    };
    const webWorkerParams = new EstimateActionWorkerMessage(
      estimateActionParams.estimateActionType,
      estimateActionData,
    );
    await this.webWorkerQueue.postMessage(webWorkerParams, callback);
  }

  async returnFromEstimateAction(message: any) {
    const data = message?.data ? message.data : {};
    const createdRecords = data?.createdRecords ? data.createdRecords : {};
    const permutationIndex = data?.permutationIndex ? data.permutationIndex : 0;
    const estimateIndex = data?.estimateIndex ? data.estimateIndex : 0;
    const estimateActions = data?.estimateActions ? data.estimateActions : [];

    // If the worker failed to run this estimate action (worker.job.ts's catch
    // path), there is no `results` to apply — skip straight to the same
    // permutationIndex/estimateIndex-driven continuation below, so this
    // failure advances the correct permutation instead of stalling
    // completedPemutations forever. It must not be silently treated as a
    // success either — record the error on the action and log it, so a
    // failed permutation is distinguishable from a completed one.
    if (data?.error) {
      const failedActionParams =
        this.permutations[permutationIndex]?.estimateActions?.[estimateIndex];
      if (failedActionParams) {
        failedActionParams.error = data.error;
      }
      this.logger.error(
        `ScenarioOption: estimate action ${estimateIndex} of permutation ${permutationIndex} failed: ${data.error}`,
      );
    } else {
      const estimateActionParams =
        this.permutations[permutationIndex].estimateActions[estimateIndex];
      // set up any records that were created from the estimate action
      if (data?.results?.createdRecords) {
        estimateActionParams.createdRecords = data.results.createdRecords;
        if (data?.results?.createdRecords?.transactions) {
          estimateActionParams.createdRecords.transactions =
            data.results.createdRecords.transactions;
          // estimateActionParams.createdRecords.transactions = DataTransformer.castToTransactionArray(
          //  data.results.createdRecords.transactions,
          // );
        }
        if (data?.results?.createdRecords?.accounts) {
          estimateActionParams.createdRecords.accounts = data.results.createdRecords.accounts;
          // estimateActionParams.createdRecords.accounts = DataTransformer.castToBookArray(
          //  data.results.createdRecords.accounts,
          // );
        }
        if (data?.results?.createdRecords?.runningAccountBalances) {
          // save and pass the groupedBalance to the next estimateAction run
          createdRecords.runningAccountBalances =
            data.results.createdRecords.runningAccountBalances;
          estimateActionParams.runningAccountBalances =
            data.results.createdRecords.runningAccountBalances;
        }
        // save and pass any created accounts to the next estimateAction run
        if (data?.results?.createdRecords?.accounts) {
          createdRecords.accounts = data.results.createdRecords.accounts;
        }
      }
    }

    // check if there are more estimate actions to run for this permutation
    if (estimateActions.length > estimateIndex + 1) {
      // run the next estimate action in the permutation
      await this.runNextEstimateAction(
        permutationIndex,
        estimateIndex + 1,
        estimateActions,
        createdRecords,
        this.returnFromEstimateAction.bind(this),
      );
      return;
    } else {
      this.completedPemutations++;
      this.completePermutation();

      // If all the permutations have been completed
      if (this.totalPermutations === this.completedPemutations) {
        await this.completedRunningAllPermutations();
      }
    }
  }

  async completedRunningAllPermutations() {
    await this.afterRunningPermutationsHook();
  }

  async getUserGeneratedEstimateTransactions(): Promise<Array<TransactionView>> {
    // TODO:
    // get list of only user generated estimate actions
    // run each estimate action with the required parameters
    // add the created transactions to the transactions array

    return [];
  }

  roundBalance(balance: number): number {
    return Math.round(balance * 1000000) / 1000000;
  }
}
